import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, MenuItem, CircularProgress, Alert,
  Autocomplete, Chip, Box, Typography, Divider,
} from '@mui/material';
import { Business as BusinessIcon, AddCircleOutline as NewIcon } from '@mui/icons-material';
import corporateService, { CorporateClient, PagaliEntidade } from '../../services/corporate.service';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: Partial<CorporateClient>;
}

const BRAND = '#6233A0';
const NOVA_ENTIDADE_OPTION: PagaliEntidade = {
  id: '__new__',
  nome: '+ Criar nova entidade Pagali',
  estado: 'ATIVO',
};

const CorporateModal: React.FC<Props> = ({ open, onClose, onSaved, initial }) => {
  const [form, setForm] = useState({
    nome: '',
    nif: '',
    contacto_nome: '',
    contacto_email: '',
    contacto_telefone: '',
    morada: '',
    plano: 'STARTER' as 'STARTER' | 'PRO' | 'ENTERPRISE',
    reward_day: 1,
  });

  const [entidadeSelected, setEntidadeSelected] = useState<PagaliEntidade | null>(null);
  const [entidadeInput, setEntidadeInput] = useState('');
  const [entidadeOptions, setEntidadeOptions] = useState<PagaliEntidade[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [criarNovaEntidade, setCriarNovaEntidade] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Limpar ao abrir
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        nome: initial.nome || '',
        nif: initial.nif || '',
        contacto_nome: initial.contacto_nome || '',
        contacto_email: initial.contacto_email || '',
        contacto_telefone: initial.contacto_telefone || '',
        morada: '',
        plano: initial.plano || 'STARTER',
        reward_day: initial.reward_day ?? 1,
      });
    } else {
      setForm({ nome: '', nif: '', contacto_nome: '', contacto_email: '', contacto_telefone: '', morada: '', plano: 'STARTER', reward_day: 1 });
    }
    setEntidadeSelected(null);
    setEntidadeInput('');
    setEntidadeOptions([]);
    setCriarNovaEntidade(false);
    setError(null);
  }, [open]);

  // Pesquisa de entidades com debounce
  const searchEntidades = useCallback(async (q: string) => {
    if (q.length < 2) { setEntidadeOptions([]); return; }
    setSearchLoading(true);
    try {
      const results = await corporateService.searchEntidades(q);
      setEntidadeOptions([...results, NOVA_ENTIDADE_OPTION]);
    } catch {
      setEntidadeOptions([NOVA_ENTIDADE_OPTION]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchEntidades(entidadeInput), 350);
    return () => clearTimeout(timer);
  }, [entidadeInput, searchEntidades]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));

  const handleEntidadeSelect = (_: any, value: PagaliEntidade | null) => {
    if (value?.id === '__new__') {
      setCriarNovaEntidade(true);
      setEntidadeSelected(null);
      return;
    }
    setCriarNovaEntidade(false);
    setEntidadeSelected(value);
    // Preencher campos com dados da entidade seleccionada
    if (value) {
      setForm(f => ({
        ...f,
        nome: f.nome || value.nome,
        nif: f.nif || value.nif || '',
        contacto_nome: f.contacto_nome || value.contacto_nome || '',
        contacto_email: f.contacto_email || value.email || '',
        contacto_telefone: f.contacto_telefone || value.telefone || '',
        morada: f.morada || value.morada || '',
      }));
    }
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { setError('Nome é obrigatório'); return; }
    setLoading(true);
    setError(null);
    try {
      const payload: any = {
        ...form,
        reward_day: Number(form.reward_day),
        entidade_id: entidadeSelected ? entidadeSelected.id : undefined,
      };
      if (initial?.id) {
        await corporateService.update(initial.id, payload);
      } else {
        await corporateService.create(payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao guardar empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial?.id ? 'Editar Empresa B2B' : 'Nova Empresa B2B'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>

          {/* ── Ligação a Entidade Pagali ── */}
          {!initial?.id && (
            <Grid item xs={12}>
              <Autocomplete
                options={entidadeOptions}
                getOptionLabel={o => o.nome}
                loading={searchLoading}
                inputValue={entidadeInput}
                onInputChange={(_, v) => setEntidadeInput(v)}
                onChange={handleEntidadeSelect}
                filterOptions={x => x}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    {option.id === '__new__' ? (
                      <Box display="flex" alignItems="center" gap={1} color={BRAND}>
                        <NewIcon fontSize="small" />
                        <Typography variant="body2" fontWeight={700}>{option.nome}</Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={600}>{option.nome}</Typography>
                          {option.nif && <Chip label={option.nif} size="small" variant="outlined" />}
                        </Box>
                        {option.email && (
                          <Typography variant="caption" color="text.secondary" ml={3}>{option.email}</Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Entidade Pagali (pesquisar ou criar nova)"
                    placeholder="Escreve nome, NIF ou email..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading && <CircularProgress size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {entidadeSelected && (
                <Alert severity="success" sx={{ mt: 1 }} icon={<BusinessIcon />}>
                  Entidade existente seleccionada — será ligada a esta empresa.
                </Alert>
              )}
              {criarNovaEntidade && (
                <Alert severity="info" sx={{ mt: 1 }} icon={<NewIcon />}>
                  Será criada uma nova entidade Pagali com os dados abaixo.
                </Alert>
              )}
            </Grid>
          )}

          <Grid item xs={12}><Divider /></Grid>

          {/* ── Dados da empresa ── */}
          <Grid item xs={12}>
            <TextField label="Nome da Empresa *" value={form.nome} onChange={set('nome')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField label="NIF" value={form.nif} onChange={set('nif')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Dia de Distribuição"
              type="number"
              value={form.reward_day}
              onChange={set('reward_day')}
              inputProps={{ min: 1, max: 28 }}
              fullWidth
              helperText="Dia do mês (1–28)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Nome do Contacto (RH)" value={form.contacto_nome} onChange={set('contacto_nome')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Email" value={form.contacto_email} onChange={set('contacto_email')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Telefone" value={form.contacto_telefone} onChange={set('contacto_telefone')} fullWidth />
          </Grid>
          {(criarNovaEntidade || !initial?.id) && (
            <Grid item xs={12}>
              <TextField label="Morada" value={form.morada} onChange={set('morada')} fullWidth />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField select label="Plano" value={form.plano} onChange={set('plano')} fullWidth>
              {(['STARTER', 'PRO', 'ENTERPRISE'] as const).map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#4e2880' } }}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {initial?.id ? 'Guardar' : (entidadeSelected ? 'Ligar & Criar' : 'Criar Empresa')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorporateModal;
